import { z } from 'zod';

import { FileSchema } from '../types/File.schema';
import { Length } from './fields/Length';
import { JobSchema } from './Job';

export enum SimulationType {
    MULTIPLE = 'quasi_steady_state',
    SNAPSHOT = 'steady_state',
}

export enum ConnectionLineType {
    'L1' = 'L1',
    'L2' = 'L2',
    'L3' = 'L3',
    'N' = 'N',
    'L+' = 'L+',
    'M' = 'M',
    'L-' = 'L-',
    'PE' = 'PE',
}

const SimulationValue = z.array(z.number().nullable());

// The fault engine emits extra internal-node entries (e.g. voltage_n_im, current_n_im) next to
// the line entries of a port. The record below types its keys as ConnectionLineType, so those
// extras must be dropped before validation or the parse fails on real engine output.
const CONNECTION_LINE_KEYS = new Set<string>(Object.values(ConnectionLineType));
const stripNonLinePortEntries = (input: unknown): unknown => {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
    return Object.fromEntries(
        Object.entries(input).filter(([key]) => CONNECTION_LINE_KEYS.has(key) || key === 'power'),
    );
};

const ProtectionMeasurement = z.object({ value: z.number(), unit: z.string() });

export const ProtectionResultSchema = z.object({
    type: z.string(),
    modelled: z.boolean(),
    tripped: z.boolean().nullable(),
    tripTime: z.number().nullable().optional(),
    peakCurrent: ProtectionMeasurement.nullish(),
    i2t: ProtectionMeasurement.nullish(),
    threshold: ProtectionMeasurement.nullish(),
    minimumVoltage: ProtectionMeasurement.optional(),
    undervoltageWindow: ProtectionMeasurement.optional(),
    reason: z.string().optional(),
});

export const ProtectionBlockSchema = z.record(z.string(), ProtectionResultSchema);

export type ProtectionResult = z.infer<typeof ProtectionResultSchema>;

const SimulationWarning = z.object({
    type: z.string(),
    message: z.string(),
});

export const SimulationResultSchema = z.object({
    componentInstances: z.record(
        z.string(),
        z.object({
            ports: z.record(
                z.string(),
                z.preprocess(
                    stripNonLinePortEntries,
                    z.intersection(
                        z.record(
                            z.nativeEnum(ConnectionLineType),
                            z.object({
                                current: SimulationValue.optional(),
                                voltage: SimulationValue.optional(),
                            }),
                        ),
                        z.object({
                            power: SimulationValue.optional(),
                        }),
                    ),
                ),
            ),
            stateOfCharge: SimulationValue.optional(),
            powerLosses: SimulationValue.optional(),
            tripped: SimulationValue.optional(),
        }),
    ),
    protection: ProtectionBlockSchema.optional(),
    warnings: z.array(SimulationWarning).optional(),
    connections: z.record(
        z.string(),
        z.object({
            power: SimulationValue.optional(),
            powerLosses: SimulationValue.optional(),
            lines: z.record(
                z.nativeEnum(ConnectionLineType),
                z.object({
                    current: SimulationValue.optional(),
                    v_arc: SimulationValue.optional(),
                    e_arc: SimulationValue.optional(),
                    e_incident: SimulationValue.optional(),
                    R_fault: SimulationValue.optional(),
                }),
            ),
            to: z.record(
                z.nativeEnum(ConnectionLineType),
                z.object({
                    current: SimulationValue.optional(),
                    voltage: SimulationValue.optional(),
                }),
            ),
            from: z.record(
                z.nativeEnum(ConnectionLineType),
                z.object({
                    current: SimulationValue.optional(),
                    voltage: SimulationValue.optional(),
                    power: SimulationValue.optional(),
                }),
            ),
        }),
    ),
    time: z.array(z.number()).optional(),
    rowsSolved: z.number().optional(),
});

export const SIMULATION_PROFILE_DATA_MAX_LENGTH = 1_000_000;

export const SimulationSchema = JobSchema.extend({
    type: z.nativeEnum(SimulationType).default(SimulationType.SNAPSHOT),
    profiles: z.array(z.string()).default([]),
    nbTimestamps: z.number().default(1),
    rowsSolved: z.number().optional(),
    result: SimulationResultSchema,
    file: z.string().optional(),
});

export const ArcFlashTimeseriesSchema = z.object({
    time: z.array(z.number()),
    arc_current: z.array(z.number()),
    arc_voltage: z.array(z.number()),
    arc_resistance: z.array(z.number()),
    conductor_gap: z.array(z.number()),
    cumulative_energy: z.array(z.number()),
    arc_length: z.array(z.number()),
});

const LengthValueUnit = z.object({ value: z.number(), unit: z.string() });
const EIncidentValueUnit = z.object({ value: z.array(z.number()), unit: z.string() });
const EArcValueUnit = z.object({ value: z.array(z.number()), unit: z.string() });

// TODO: drop this adapter once the dcide-sim arc-flash response refactor lands.
// The new sim contract drops the `arc_` prefix on scalar power values, wraps lengths and incident
// energy in {value, unit} objects, returns e_incident as an array aligned to top-level time, and
// replaces t_validity_end with minimum_arc_current. The OLD shape is still live on staging/prod
// and is also what older MongoDB docs contain. This preprocess reshapes old payloads to the new
// shape so a single schema serves both during the rollover.
const reshapeLegacyArcFlash = (input: unknown): unknown => {
    if (!input || typeof input !== 'object') return input;
    const raw = input as Record<string, unknown>;
    const out: Record<string, unknown> = { ...raw };

    if (typeof out.arc_current === 'number' && out.current === undefined) out.current = out.arc_current;
    if (typeof out.arc_voltage === 'number' && out.voltage === undefined) out.voltage = out.arc_voltage;
    if (typeof out.arc_power === 'number' && out.power === undefined) out.power = out.arc_power;
    if (typeof out.arc_energy === 'number' && out.energy === undefined) out.energy = out.arc_energy;
    if (typeof out.arc_power_avg === 'number' && out.power_avg === undefined) out.power_avg = out.arc_power_avg;

    if (typeof out.working_distance === 'number') {
        out.working_distance = { value: out.working_distance, unit: 'm' };
    }
    if (typeof out.arc_flash_boundary === 'number') {
        out.arc_flash_boundary = {
            value: out.arc_flash_boundary,
            unit: typeof out.arc_flash_boundary_unit === 'string' ? out.arc_flash_boundary_unit : 'm',
        };
    }
    if (typeof out.e_incident === 'number') {
        out.e_incident = {
            value: [out.e_incident],
            unit: typeof out.e_incident_unit === 'string' ? out.e_incident_unit : 'J/m²',
        };
    }

    return out;
};

export const ArcFlashResultSchema = z.preprocess(
    reshapeLegacyArcFlash,
    z
        .object({
            method: z.string(),
            current: z.number().optional(),
            voltage: z.number().optional(),
            power: z.number().optional(),
            power_avg: z.number().optional(),
            energy: z.number(),
            arc_duration: z.number(),
            conductor_gap: z.number().optional(),
            initial_conductor_gap: z.number().optional(),
            final_conductor_gap: z.number().optional(),
            working_distance: LengthValueUnit,
            enclosure_type: z.string(),
            enclosure_factor: z.number(),
            // Wilkins incident-energy formula E_i = k * E_arc / (a^2 + d^x). `a` (distance constant) and
            // `x` (distance exponent) are pending on the sim side (DEV-9014). Until then the badge falls
            // back to symbolic placeholders for those terms.
            wilkins_a: z.number().optional(),
            wilkins_x: z.number().optional(),
            e_incident: EIncidentValueUnit,
            e_arc: EArcValueUnit.optional(),
            arc_flash_boundary: LengthValueUnit,
            minimum_arc_current: z.number().optional(),
            minimum_arc_current_unit: z.string().optional(),
            ppe_category: z.number(),
            system_voltage: z.number().optional(),
            bolted_fault_current: z.number().optional(),
            erosion_enabled: z.boolean().optional(),
            conductor_material: z.string().optional(),
            electrode_geometry: z.string().optional(),
            timeseries: ArcFlashTimeseriesSchema.optional(),
        })
        .passthrough(),
);

const FaultMetric = z.object({ value: z.number(), unit: z.string() });

export const FaultMetricSetSchema = z.object({
    peakCurrent: FaultMetric,
    diDt_10_90: FaultMetric,
    i2t: FaultMetric,
});

// Per the dcide-sim fault output contract: the top level is the nominal solve, `scenarios` maps
// each requested operating scenario (min/max severity) to a subtree shaped like the top level,
// and `faultMetrics` always carries nom plus one entry per requested scenario. Both keys are
// optional here because results stored before the contract landed lack them.
export const FaultSimulationResultSchema = SimulationResultSchema.extend({
    time: z.array(z.number()),
    arcFlash: ArcFlashResultSchema.optional(),
    scenarios: z
        .object({
            min: SimulationResultSchema.optional(),
            max: SimulationResultSchema.optional(),
        })
        .optional(),
    faultMetrics: z
        .object({
            nom: FaultMetricSetSchema,
            min: FaultMetricSetSchema.optional(),
            max: FaultMetricSetSchema.optional(),
        })
        .optional(),
});

export const FaultSimulationSummarySchema = z.object({
    peakFaultCurrent: z.number().nullable(),
    arcFlash: z
        .object({
            ppeCategory: z.number(),
            incidentEnergyCalCm2: z.number(),
        })
        .nullable()
        .optional(),
    touchVoltage: z
        .object({
            from: z.number().optional(),
            to: z.number().optional(),
        })
        .nullable()
        .optional(),
});

export const FaultSimulationSchema = JobSchema.extend({
    nbTimestamps: z.number().default(1),
    result: FaultSimulationResultSchema,
    resultFile: FileSchema.optional(),
    faultConnectionId: z.string(),
    faultTime: z.number().min(0.001, 'Fault time must be greater than 0').default(0.001),
    faultResistance: z.number().min(0),
    faultLocation: z.number().min(0).max(1),
    faultLines: z.array(z.enum(['L+', 'M', 'L-', 'PE'])).default(['L+', 'L-']),
    // Opt-in: also solve the min/max component-parameter scenarios (band + doubled charge).
    runMinMaxScenarios: z.boolean().default(false),
    enableProtectionTripping: z.boolean().default(false),
    simulationDuration: z.number().min(0.001).max(1.0, 'Maximum duration is 1 second').default(0.2),
    batchId: z.string().optional(),
    batchName: z.string().optional(),
    hash: z.string().optional(),
    arcModel: z.enum(['none', 'maximum_power', 'stokes_oppenlander', 'doan', 'landry-marroquin']).default('none'),
    conductorGap: Length.validator.default({ value: 0.025, unit: 'm' }),
    workingDistance: Length.validator.default({ value: 0.457, unit: 'm' }),
    enclosureType: z.enum(['open-air', 'panelboard', 'battery-cabinet', 'generic-box']).default('open-air'),
    minVoltage: z
        .object({
            value: z.number(),
            unit: z.string(),
        })
        .optional(),
    initialConductorGap: z.number().min(0).optional(),
    conductorMaterial: z.enum(['Cu', 'Al', 'Ag']).optional(),
    electrodeGeometry: z.enum(['parallel-plate']).optional(),
    electrodeArea: z.number().min(0).optional(),
    erosion: z.boolean().optional(),
    includeTimeseries: z.boolean().optional(),
    summary: FaultSimulationSummarySchema.optional(),
    // Set by the result webhook when the engine result contains min/max scenarios.
    hasMinMaxScenarios: z.boolean().optional(),
})
    .refine((data) => data.faultTime < data.simulationDuration, {
        message: 'Fault time must be less than simulation duration',
        path: ['faultTime'],
    })
    .refine(
        (data) => {
            if (data.arcModel !== 'landry-marroquin') return true;
            return data.initialConductorGap === undefined || data.initialConductorGap > 0;
        },
        {
            message: 'Initial conductor gap must be positive',
            path: ['initialConductorGap'],
        },
    )
    .refine(
        (data) => {
            if (data.arcModel !== 'landry-marroquin') return true;
            return data.electrodeArea === undefined || data.electrodeArea > 0;
        },
        {
            message: 'Electrode area must be positive',
            path: ['electrodeArea'],
        },
    );

// Define TypeScript types for the possible return types
export type SimulationResponse = z.infer<typeof SimulationResultSchema>;
